import React, { createContext, useState, useContext, useEffect } from 'react';
import api from '../services/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const initializeAuth = async () => {
            const token = localStorage.getItem('authToken');
            if (token) {
                try {
                    const response = await api.get('/auth/me');
                    const meUser = response.data.data.user; // normalize
                    // Add avatar
                    const updatedUser = {
                        ...meUser,
                        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(meUser.full_name)}&background=10b981&color=fff`,
                    };
                    setUser(updatedUser);
                    localStorage.setItem('userData', JSON.stringify(updatedUser));
                } catch (error) {
                    console.error("Failed to fetch user on load", error);
                    logout();
                }
            }
            setLoading(false);
        };
        initializeAuth();
    }, []);

    const auth = async (email, password, isRegister = false, fullName = '') => {
        try {
            let response;
            if (isRegister) {
                // Call your register API
                response = await api.post("/auth/register", {
                    full_name: fullName,
                    email,
                    password,
                });
            } else {
                // Call your login API
                response = await api.post("/auth/login", { email, password });
            }
            
            const { token, user } = response.data.data;

            // Add dynamic avatar
            const updatedUser = {
                ...user,
                avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name)}&background=10b981&color=fff`,
            };

            localStorage.setItem("authToken", token);
            localStorage.setItem("userData", JSON.stringify(updatedUser));

            setUser(updatedUser);

            return { success: true, user: updatedUser };
        } catch (error) {
            logout(); // Ensure clean state on login failure
            throw error;
        }
    };
    
    const logout = () => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('userData');
        setUser(null);
    };
    
    const hasRole = (roleName) => {
        if (!user || !user.role) return false;
        return Array.isArray(roleName)
            ? roleName.includes(user.role.name)
            : user.role.name === roleName;
    };

    const updateUser = (data) => {
        const updatedUser = { ...user, ...data };
        localStorage.setItem('userData', JSON.stringify(updatedUser));
        setUser(updatedUser);
    };

    const isAdmin = () => user?.role === 'Super Admin';

    const value = {
        user,
        isAuthenticated: !!user,
        loading,
        auth,
        logout,
        updateUser,
        isAdmin,
        hasRole,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};